/** A room's Audience, read and acted on: its groups for a live session or all time, and the private send to a group. */
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import type { RoomAudienceResponse, RoomGroup, RoomGroupKey, WebinarDetail } from "../../../shared/types";
import { api } from "../../lib/api";
import { lastRun, longDay, shortDay } from "./liveWords";
import { type PrivateSend, startMessage } from "./PrivateSendSheet";
import { groupWords, missedReplayLine } from "./roomWords";

export type AudienceScope = "session" | "all";

export function useAudience(detail: WebinarDetail, hostFirst: string) {
  const location = useLocation();
  const asked = location.state as { group?: RoomGroupKey; session?: string } | null;
  const series = detail.live?.series ?? null;
  const last = series ? lastRun(series.sessions) : null;
  // Opened from an older session's report, the groups are that session's.
  const chosen = (asked?.session && series?.sessions.find((x) => x.id === asked.session && x.state === "ended")) || last;
  const [scope, setScope] = useState<AudienceScope>(chosen ? "session" : "all");
  const [data, setData] = useState<RoomAudienceResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState<RoomGroupKey | null>(asked?.group ?? null);
  const [sending, setSending] = useState<PrivateSend | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    api.webinars
      .audience(detail.id, scope === "all" ? (series ? "series" : "all") : chosen?.id)
      .then((r) => {
        if (!alive) return;
        setData(r);
        setPicked((was) => (was && r.groups.some((g) => g.key === was) ? was : (r.groups[0]?.key ?? null)));
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [detail.id, scope, chosen?.id, nonce]);

  const session = data?.scope === "session" ? data.session : null;
  const when = session ? longDay(session.startsAt, session.timezone) : detail.title;

  // Missed it's line about the replay, from the records: when it was emailed and who pressed play.
  const replayLine = (g: RoomGroup) =>
    g.key === "missed" && session ? missedReplayLine(g.played, session.replayMail, session.timezone, Date.now()) : "";

  const wordsOf = (key: RoomGroupKey) => groupWords(key, data?.scope ?? scope, detail, Date.now(), !!data?.slides);

  const startSend = (g: RoomGroup) => {
    const auto = replayLine(g);
    const words = wordsOf(g.key);
    setSending({
      seed: { webinarId: detail.id, scope: data?.scope ?? scope, key: g.key, ...(session ? { session: session.id } : {}) },
      title: words.act(g.reach) ? (g.sent ? words.again(g.reach) : words.act(g.reach)) : words.label,
      ...(words.act(g.reach) && g.reach > 0 ? {} : { circleOnly: true }),
      reach: g.reach,
      faces: g.faces,
      label: `${words.label}, ${when}`,
      subject: words.subject,
      message: startMessage(words.message, hostFirst),
      ...(g.key === "came" && data?.slides ? { slides: true } : {}),
      ...(auto ? { auto } : {}),
    });
  };

  const wideLabel = series ? "All sessions" : "All time";
  const segLabel = chosen
    ? `${chosen.id === last?.id ? "Last live" : `Session ${chosen.seq}`} · ${shortDay(chosen.startsAt, chosen.timezone)}`
    : "";

  return {
    chosen,
    scope,
    setScope,
    data,
    failed,
    retry: () => setNonce((x) => x + 1),
    picked,
    setPicked,
    sending,
    closeSend: () => setSending(null),
    startSend,
    replayLine,
    wordsOf,
    segLabel,
    wideLabel,
  };
}
